import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { DailyEmailsCard } from './DailyEmailsCard';
import { ContentBackupCard } from './ContentBackupCard';
import {
  Settings, Mail, Shield, BookOpen, ToggleLeft, Save, Loader2, CreditCard,
  MenuIcon, Tag, Palette, Users,
} from 'lucide-react';

const SystemSettingsPage = () => {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('general');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      setSettings(res.data);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading || !settings) return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="system-settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">System Settings</h1>
            <p className="text-slate-500 mt-1 text-sm">Configure platform behaviour</p>
          </div>
          <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save All
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap">
            <TabsTrigger value="general" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Settings className="h-3.5 w-3.5" /> General</TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><ToggleLeft className="h-3.5 w-3.5" /> Features</TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Course Defaults</TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Shield className="h-3.5 w-3.5" /> Security</TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Mail className="h-3.5 w-3.5" /> Email</TabsTrigger>
            <TabsTrigger value="payment" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Payment</TabsTrigger>
            <TabsTrigger value="navigation" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><MenuIcon className="h-3.5 w-3.5" /> Navigation</TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Palette className="h-3.5 w-3.5" /> Branding</TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Tag className="h-3.5 w-3.5" /> Promo Codes</TabsTrigger>
            <TabsTrigger value="onboarding" className="data-[state=active]:bg-[#20acf6] data-[state=active]:text-white gap-1.5"><Users className="h-3.5 w-3.5" /> Onboarding</TabsTrigger>
          </TabsList>

          {/* ─── GENERAL ─── */}
          <TabsContent value="general">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">General Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Site Name</Label>
                    <Input value={settings.platform_name || ''} onChange={e => update('platform_name', e.target.value)} data-testid="setting-site-name" />
                  </div>
                  <div>
                    <Label>Support Email</Label>
                    <Input value={settings.support_email || ''} onChange={e => update('support_email', e.target.value)} data-testid="setting-support-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Email</Label>
                    <Input value={settings.contact_email || ''} onChange={e => update('contact_email', e.target.value)} />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input value={settings.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Brand Colour</Label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={settings.primary_brand_color || '#20acf6'} onChange={e => update('primary_brand_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                      <Input value={settings.primary_brand_color || ''} onChange={e => update('primary_brand_color', e.target.value)} className="flex-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Secondary Brand Colour</Label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={settings.secondary_brand_color || '#02d1d4'} onChange={e => update('secondary_brand_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                      <Input value={settings.secondary_brand_color || ''} onChange={e => update('secondary_brand_color', e.target.value)} className="flex-1" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── FEATURES ─── */}
          <TabsContent value="features">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Feature Toggles</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                {[
                  { key: 'enable_chatbot', label: 'Chatbot (Ellie)', desc: 'Show AI chatbot assistant across the platform' },
                  { key: 'enable_learning_quiz', label: 'Learning Style Quiz', desc: 'Allow learners to complete the learning style assessment' },
                  { key: 'enable_text_size', label: 'Text Size Toggle', desc: 'Show text size accessibility control' },
                  { key: 'enable_public_courses', label: 'Public Course Catalogue', desc: 'Make course listings visible without login' },
                  { key: 'enable_self_registration', label: 'Self Registration', desc: 'Allow anyone to register on the platform' },
                  { key: 'maintenance_mode', label: 'Maintenance Mode', desc: 'Put the platform into maintenance mode (admins only)' },
                ].map(f => (
                  <div key={f.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{f.label}</p>
                      <p className="text-xs text-slate-500">{f.desc}</p>
                    </div>
                    <Switch checked={settings[f.key] ?? true} onCheckedChange={v => update(f.key, v)} data-testid={`toggle-${f.key}`} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── COURSE DEFAULTS ─── */}
          <TabsContent value="courses" className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Course Default Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Default Passing Score (%)</Label>
                    <Input type="number" min={0} max={100} value={settings.default_passing_score ?? 85} onChange={e => update('default_passing_score', parseInt(e.target.value) || 85)} data-testid="setting-passing-score" />
                  </div>
                  <div>
                    <Label>Default Max Quiz Attempts</Label>
                    <Input type="number" min={1} max={10} value={settings.default_max_quiz_attempts ?? 3} onChange={e => update('default_max_quiz_attempts', parseInt(e.target.value) || 3)} />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Auto-issue Certificates</p>
                    <p className="text-xs text-slate-500">Automatically issue certificates when courses are completed</p>
                  </div>
                  <Switch checked={settings.auto_issue_certificates ?? true} onCheckedChange={v => update('auto_issue_certificates', v)} data-testid="toggle-auto-cert" />
                </div>
              </CardContent>
            </Card>
            <ContentBackupCard />
          </TabsContent>

          {/* ─── SECURITY ─── */}
          <TabsContent value="security">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Security Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Session Timeout (minutes)</Label>
                    <Input type="number" min={5} max={1440} value={settings.session_timeout_minutes ?? 60} onChange={e => update('session_timeout_minutes', parseInt(e.target.value) || 60)} data-testid="setting-session-timeout" />
                  </div>
                  <div>
                    <Label>Max Login Attempts</Label>
                    <Input type="number" min={1} max={20} value={settings.max_login_attempts ?? 5} onChange={e => update('max_login_attempts', parseInt(e.target.value) || 5)} />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Note: These settings are stored in the database. Full enforcement requires backend implementation.</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── EMAIL ─── */}
          <TabsContent value="email">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Email Configuration</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Email Provider</Label>
                  <Select value={settings.email_provider || 'console'} onValueChange={v => update('email_provider', v)}>
                    <SelectTrigger data-testid="setting-email-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="console">Console (Development)</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="smtp">SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Sender Email</Label>
                    <Input value={settings.sender_email || ''} onChange={e => update('sender_email', e.target.value)} placeholder="noreply@commonimpact.co.uk" />
                  </div>
                  <div>
                    <Label>Sender Name</Label>
                    <Input value={settings.sender_name || ''} onChange={e => update('sender_name', e.target.value)} />
                  </div>
                </div>
                {settings.email_provider === 'smtp' && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>SMTP Host</Label><Input value={settings.smtp_host || ''} onChange={e => update('smtp_host', e.target.value)} /></div>
                    <div><Label>SMTP Port</Label><Input type="number" value={settings.smtp_port || 587} onChange={e => update('smtp_port', parseInt(e.target.value))} /></div>
                  </div>
                )}
                <p className="text-xs text-slate-500 p-3 bg-amber-50 rounded-lg">When a SendGrid API key is set on the server, emails always send through SendGrid.</p>
              </CardContent>
            </Card>
            <DailyEmailsCard />
          </TabsContent>

          {/* ─── PAYMENT ─── */}
          <TabsContent value="payment">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Payment Configuration</CardTitle></CardHeader>
              <CardContent>
                <div className="p-6 bg-slate-50 rounded-lg text-center">
                  <CreditCard className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 mb-1">Stripe payment integration</p>
                  <p className="text-xs text-slate-500">Connect your Stripe API key via environment variables on your deployment platform (Render). Manage Stripe keys in your server's <code className="bg-slate-200 px-1 rounded">.env</code> file.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── NAVIGATION ─── */}
          <TabsContent value="navigation">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Navigation Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">Manage your site navigation menus. For advanced navigation editing, visit the <a href="/admin/navigation" className="text-[#20acf6] hover:underline font-medium">Navigation Editor</a>.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Show Pricing Page</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <Switch checked={settings.show_pricing || false} onCheckedChange={v => update('show_pricing', v)} />
                      <span className="text-sm text-slate-600">{settings.show_pricing ? 'Visible' : 'Hidden'}</span>
                    </div>
                  </div>
                  <div>
                    <Label>Show About Page</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <Switch checked={settings.show_about !== false} onCheckedChange={v => update('show_about', v)} />
                      <span className="text-sm text-slate-600">{settings.show_about !== false ? 'Visible' : 'Hidden'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── BRANDING ─── */}
          <TabsContent value="branding">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Branding</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">Customise your platform branding. For the full branding editor, visit <a href="/admin/branding" className="text-[#20acf6] hover:underline font-medium">Branding Page</a>.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Colour</Label>
                    <Input value={settings.primary_color || '#20acf6'} onChange={e => update('primary_color', e.target.value)} />
                  </div>
                  <div>
                    <Label>Accent Colour</Label>
                    <Input value={settings.accent_color || '#02d1d4'} onChange={e => update('accent_color', e.target.value)} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── PROMO CODES ─── */}
          <TabsContent value="promo">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Promo Codes</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">Create and manage promo codes for course discounts.</p>
                <a href="/admin/promo-codes">
                  <Button className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="open-promo-codes-btn">
                    <Tag className="h-4 w-4 mr-2" /> Open Promo Codes Manager
                  </Button>
                </a>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── ONBOARDING ─── */}
          <TabsContent value="onboarding">
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-lg">Onboarding Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Show Learning Style Quiz</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch checked={settings.enable_learning_quiz !== false} onCheckedChange={v => update('enable_learning_quiz', v)} />
                    <span className="text-sm text-slate-600">Show the learning style quiz on first login</span>
                  </div>
                </div>
                <div>
                  <Label>Welcome Message</Label>
                  <Input value={settings.welcome_message || ''} onChange={e => update('welcome_message', e.target.value)} placeholder="Welcome to Common Impact E-Learning!" />
                </div>
                <div>
                  <Label>Default Role for New Users</Label>
                  <Select value={settings.default_role || 'learner'} onValueChange={v => update('default_role', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="learner">Learner</SelectItem>
                      <SelectItem value="org_admin">Org Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SystemSettingsPage;
